import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  KpiCard,
  NoPermissionState,
  Notice,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '@stayafrica/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Banknote,
  Coins,
  History,
  Lock,
  Plus,
  Scale,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import {
  CashMovementDrawer,
  CloseCashSessionDialog,
  OpenCashSessionDialog,
  ReconcileCashSessionDialog,
} from '@/features/payments/CashDialogs';
import { CashMovementLabel } from '@/features/payments/CashMovementLabel';
import { cashOverviewQueryOptions, cashSessionsQueryOptions } from '@/features/payments/queries';
import { cashSessionTone, movementTone } from '@/features/payments/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateTime, formatTime } from '@/lib/format';

export const Route = createFileRoute('/_app/billing/cash/')({
  component: CashPage,
});

/**
 * Caisse (SCR-093) : état de la session courante, mouvements du jour,
 * ouverture / clôture / rapprochement, puis historique des sessions.
 *
 * Sans session ouverte, les paiements en espèces sont refusés par l'API
 * (`409 CASH_SESSION_NOT_OPEN`, FL-005) : le bandeau le rappelle explicitement.
 */
function CashPage() {
  const { t, i18n } = useTranslation(['cash', 'payments', 'expenses', 'common', 'errors']);
  const session = useSession();
  const canRead = session.can('cash.read');

  const [openOpen, setOpenOpen] = useState(false);
  const [movementOpen, setMovementOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);

  const overview = useQuery(cashOverviewQueryOptions(canRead));
  const history = useInfiniteQuery(cashSessionsQueryOptions({}, 10, canRead));

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (overview.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={120} />
          <Skeleton height={300} />
        </div>
      </Container>
    );
  }

  if (overview.isError || !overview.data) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(overview.error, t)}
          traceId={errorTraceId(overview.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void overview.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const current = overview.data.currentSession;
  const movements = overview.data.recentMovements;
  const sessions = history.data?.pages.flatMap((page) => page.data) ?? [];
  const needsReconcile =
    current === null &&
    sessions.some(
      (item) =>
        item.status === 'CLOSED' && (item.variance ?? 0) !== 0 && item.reconciledAt === null,
    );
  const pendingVariance = sessions.find(
    (item) => item.status === 'CLOSED' && (item.variance ?? 0) !== 0 && item.reconciledAt === null,
  );

  return (
    <Container>
      <PageHeader
        title={t('cash:title')}
        subtitle={t('cash:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('cash:title') },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            <Button variant="secondary" asChild>
              <Link to="/billing/payments">
                <Banknote className="ico" aria-hidden="true" />
                {t('payments:list.title')}
              </Link>
            </Button>
            {session.can('expense.read') ? (
              <Button variant="secondary" asChild>
                <Link to="/finance/expenses">
                  <Coins className="ico" aria-hidden="true" />
                  {t('expenses:title')}
                </Link>
              </Button>
            ) : null}
            {current ? (
              <>
                {session.can('cash.record_movement') ? (
                  <Button variant="secondary" onClick={() => setMovementOpen(true)}>
                    <Plus className="ico" aria-hidden="true" />
                    {t('cash:actions.recordMovement')}
                  </Button>
                ) : null}
                {session.can('cash.close_session') ? (
                  <Button variant="primary" onClick={() => setCloseOpen(true)}>
                    <Lock className="ico" aria-hidden="true" />
                    {t('cash:actions.closeSession')}
                  </Button>
                ) : null}
              </>
            ) : session.can('cash.open_session') ? (
              <Button variant="primary" onClick={() => setOpenOpen(true)}>
                <Wallet className="ico" aria-hidden="true" />
                {t('cash:actions.openSession')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="stack">
        {/* ------------------------- Session courante ------------------------- */}
        {current === null ? (
          <Notice
            tone="warning"
            title={t('cash:closedTitle')}
            icon={<Wallet className="ico ico-20 notice__ico" aria-hidden="true" />}
            actions={
              session.can('cash.open_session') ? (
                <Button variant="primary" size="sm" onClick={() => setOpenOpen(true)}>
                  {t('cash:actions.openSession')}
                </Button>
              ) : null
            }
          >
            {t('cash:closedBody')}
          </Notice>
        ) : (
          <>
            <div className="grid grid-4">
              <KpiCard
                tone="blue"
                icon={<Wallet className="ico ico-16" aria-hidden="true" />}
                label={t('cash:fields.openingBalance')}
                value={formatAmount(current.openingBalance, i18n.language)}
                footer={t('cash:openedAt', {
                  time: formatTime(current.openedAt, i18n.language),
                  author: current.openedByName ?? '—',
                })}
              />
              <KpiCard
                tone="green"
                icon={<ArrowDownLeft className="ico ico-16" aria-hidden="true" />}
                label={t('cash:fields.ins')}
                value={formatAmount(current.totals.payments + current.totals.ins, i18n.language)}
                footer={t('cash:kpi.paymentsFoot', {
                  amount: formatAmount(current.totals.payments, i18n.language),
                })}
              />
              <KpiCard
                tone="amber"
                icon={<ArrowUpRight className="ico ico-16" aria-hidden="true" />}
                label={t('cash:fields.outs')}
                value={formatAmount(
                  current.totals.outs + current.totals.expenses + current.totals.refunds,
                  i18n.language,
                )}
                footer={t('cash:kpi.expensesFoot', {
                  amount: formatAmount(current.totals.expenses, i18n.language),
                })}
              />
              <KpiCard
                tone="violet"
                icon={<Scale className="ico ico-16" aria-hidden="true" />}
                label={t('cash:fields.expectedBalance')}
                value={formatAmount(current.expectedBalance, i18n.language)}
                footer={
                  <Badge tone={cashSessionTone(current.status)} dot>
                    {t(`cash:status.${current.status}`)}
                  </Badge>
                }
              />
            </div>

            <Card>
              <CardHeader
                title={t('cash:movementsTitle')}
                icon={<History className="ico ico-16" aria-hidden="true" />}
                iconTone="blue"
                actions={
                  <Badge tone="neutral">
                    {t('cash:movementsCount', { count: current.totals.movementsCount })}
                  </Badge>
                }
              />
              <CardBody density="flush">
                {movements.length === 0 ? (
                  <div className="card__body">
                    <EmptyState
                      icon={<History className="ico ico-24" aria-hidden="true" />}
                      title={t('cash:movementsEmptyTitle')}
                      description={t('cash:movementsEmptyBody')}
                    />
                  </div>
                ) : (
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <Th>{t('cash:columns.time')}</Th>
                          <Th>{t('cash:columns.kind')}</Th>
                          <Th>{t('cash:columns.description')}</Th>
                          <Th align="right">{t('cash:columns.amount')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {movements.map((movement) => (
                          <Tr key={movement.id}>
                            <Td className="t-13" numeric>
                              {formatTime(movement.occurredAt, i18n.language)}
                              <div className="t-12 muted">{movement.createdByName ?? '—'}</div>
                            </Td>
                            <Td>
                              <Badge tone={movementTone(movement.kind)}>
                                {t(`cash:movementKind.${movement.kind}`)}
                              </Badge>
                            </Td>
                            <Td className="t-13">
                              <CashMovementLabel movement={movement} />
                            </Td>
                            <Td align="right" numeric>
                              <span
                                className="strong"
                                style={{
                                  color:
                                    movement.direction === 'OUT'
                                      ? 'var(--danger)'
                                      : 'var(--success)',
                                }}
                              >
                                {movement.direction === 'OUT' ? '−' : '+'}
                                {formatAmount(movement.amount, i18n.language)}
                              </span>
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </CardBody>
            </Card>
          </>
        )}

        {/* Les dépenses ont quitté la caisse (D-052 / A-058) : rappel permanent. */}
        <Notice
          tone="info"
          icon={<Coins className="ico ico-20 notice__ico" aria-hidden="true" />}
          title={t('cash:expensesNoticeTitle')}
          actions={
            session.can('expense.read') ? (
              <Button variant="secondary" size="sm" asChild>
                <Link to="/finance/expenses">{t('expenses:actions.viewExpenses')}</Link>
              </Button>
            ) : null
          }
        >
          {t('cash:expensesNoticeBody')}
        </Notice>

        {/* Écart non justifié : le rapprochement reste à faire (cash.reconcile). */}
        {needsReconcile && pendingVariance ? (
          <Notice
            tone="danger"
            title={t('cash:reconcile.pendingTitle')}
            icon={<Scale className="ico ico-20 notice__ico" aria-hidden="true" />}
            actions={
              session.can('cash.reconcile') ? (
                <Button variant="secondary" size="sm" onClick={() => setReconcileOpen(true)}>
                  {t('cash:actions.reconcile')}
                </Button>
              ) : null
            }
          >
            {t('cash:reconcile.pendingBody', {
              amount: formatAmount(pendingVariance.variance ?? 0, i18n.language),
              date: formatDateTime(
                pendingVariance.closedAt ?? pendingVariance.openedAt,
                i18n.language,
              ),
            })}
          </Notice>
        ) : null}

        {/* --------------------------- Historique ---------------------------- */}
        <Card>
          <CardHeader
            title={t('cash:historyTitle')}
            icon={<History className="ico ico-16" aria-hidden="true" />}
          />
          <CardBody density="flush">
            {sessions.length === 0 ? (
              <div className="card__body">
                <EmptyState
                  icon={<Wallet className="ico ico-24" aria-hidden="true" />}
                  title={t('cash:historyEmptyTitle')}
                />
              </div>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('cash:columns.openedAt')}</Th>
                      <Th>{t('cash:columns.openedBy')}</Th>
                      <Th align="right">{t('cash:columns.opening')}</Th>
                      <Th align="right">{t('cash:columns.expected')}</Th>
                      <Th align="right">{t('cash:columns.counted')}</Th>
                      <Th align="right">{t('cash:columns.variance')}</Th>
                      <Th>{t('cash:columns.status')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((item) => (
                      <Tr key={item.id}>
                        <Td className="t-13">
                          <Link to="/billing/cash/$sessionId" params={{ sessionId: item.id }}>
                            <span className="cell-id">
                              {formatDateTime(item.openedAt, i18n.language)}
                            </span>
                          </Link>
                        </Td>
                        <Td className="t-13">{item.openedByName ?? '—'}</Td>
                        <Td align="right" numeric>
                          {formatAmount(item.openingBalance, i18n.language)}
                        </Td>
                        <Td align="right" numeric>
                          {formatAmount(item.expectedBalance, i18n.language)}
                        </Td>
                        <Td align="right" numeric>
                          {item.actualBalance === null
                            ? '—'
                            : formatAmount(item.actualBalance, i18n.language)}
                        </Td>
                        <Td align="right" numeric>
                          {item.variance === null ? (
                            '—'
                          ) : (
                            <span
                              className="strong"
                              style={{
                                color:
                                  item.variance === 0
                                    ? 'var(--success)'
                                    : item.variance > 0
                                      ? 'var(--warning)'
                                      : 'var(--danger)',
                              }}
                            >
                              {formatAmount(item.variance, i18n.language)}
                            </span>
                          )}
                        </Td>
                        <Td>
                          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                            <Badge tone={cashSessionTone(item.status)} dot>
                              {t(`cash:status.${item.status}`)}
                            </Badge>
                            {item.reconciledAt ? (
                              <Badge tone="violet">{t('cash:status.reconciled')}</Badge>
                            ) : null}
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardBody>
          {history.hasNextPage ? (
            <div className="card__foot">
              <Button
                variant="secondary"
                size="sm"
                loading={history.isFetchingNextPage}
                onClick={() => void history.fetchNextPage()}
              >
                {t('common:pagination.loadMore')}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>

      <OpenCashSessionDialog open={openOpen} onOpenChange={setOpenOpen} />
      {current ? (
        <>
          <CashMovementDrawer
            open={movementOpen}
            onOpenChange={setMovementOpen}
            session={current}
          />
          <CloseCashSessionDialog open={closeOpen} onOpenChange={setCloseOpen} session={current} />
        </>
      ) : null}
      {pendingVariance ? (
        <ReconcileCashSessionDialog
          open={reconcileOpen}
          onOpenChange={setReconcileOpen}
          session={pendingVariance}
        />
      ) : null}
    </Container>
  );
}
